import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Button,
  Card,
  Row,
  Col,
  Input,
  Table,
  Space,
  Tooltip,
  Popconfirm,
  Select,
  Tag,
  Spin,
  message
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { complaintsService, Complaint } from '../services/complaintsService';
import { useAuth } from '../contexts/AuthContext';
import { exportToCSV } from '../utils/exportUtils';

const statusMap = {
  open: { label: 'Ouvert', color: 'orange' },
  in_progress: { label: 'En cours', color: 'blue' },
  resolved: { label: 'Résolu', color: 'green' },
  rejected: { label: 'Rejeté', color: 'red' },
  on_hold: { label: 'En attente', color: 'purple' },
} as const;

const priorityMap = {
  low: { label: 'Basse', color: 'blue' },
  medium: { label: 'Moyenne', color: 'orange' },
  high: { label: 'Haute', color: 'volcano' },
  critical: { label: 'Critique', color: 'red' },
} as const;

export const RECURRENT_MOTIFS = [
  { value: 'retard', label: "Retard d'intervention", keywords: ['retard', 'heure', 'attente', 'en retard'] },
  { value: 'qualite', label: 'Qualité de prestation', keywords: ['qualité', 'prestation', 'mal fait', 'fuite', 'bruit', 'odeur', 'travail'] },
  { value: 'facturation', label: 'Facturation / Devis', keywords: ['facture', 'devis', 'prix', 'montant', 'coût', 'paiement', 'remboursement', 'cher'] },
  { value: 'comportement', label: 'Comportement technicien', keywords: ['comportement', 'attitude', 'impoli', 'technicien', 'politesse'] },
  { value: 'panne_recurrente', label: 'Panne récurrente', keywords: ['panne', 'encore', 'toujours', 'ne marche pas', 'ne refroidit pas', 'récidive'] },
  { value: 'application', label: 'Problème application / Paiement', keywords: ['application', 'app', 'bug', 'connexion', 'otp', 'sms', 'bloqué'] },
  { value: 'autre', label: 'Autre motif', keywords: [] },
];

export const detectComplaintMotif = (complaint: Complaint): string => {
  const cat = (complaint as any).category;
  if (cat) {
    const found = RECURRENT_MOTIFS.find(m => m.value === cat || m.label.toLowerCase() === cat.toLowerCase());
    if (found) return found.label;
    return cat;
  }
  const text = `${complaint.subject || ''} ${complaint.description || ''}`.toLowerCase();
  for (const m of RECURRENT_MOTIFS) {
    if (m.keywords.some(k => text.includes(k))) {
      return m.label;
    }
  }
  return 'Autre motif';
};

const ComplaintsPage: React.FC = () => {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [motifFilter, setMotifFilter] = useState<string>('');
  const [exporting, setExporting] = useState<boolean>(false);
  
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const fetchComplaints = useCallback(async () => {
    try {
      setLoading(true);
      const response = await complaintsService.getComplaints({
        status: statusFilter,
        priority: priorityFilter,
        search: searchTerm,
        limit: 1000,
      });
      setComplaints(response.data.complaints || []);
    } catch (error) {
      message.error('Erreur lors du chargement des réclamations');
      console.error('Error fetching complaints:', error);
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, searchTerm]);

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  const handleDelete = async (id: number) => {
    try {
      await complaintsService.deleteComplaint(id);
      message.success('Réclamation supprimée avec succès');
      fetchComplaints();
    } catch (error) {
      message.error('Erreur lors de la suppression de la réclamation');
    }
  };

  const filteredComplaints = complaints.filter((complaint) => {
    if (searchTerm) {
      const matchSearch =
        complaint.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        complaint.id?.toString().includes(searchTerm) ||
        complaint.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        complaint.description?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchSearch) return false;
    }

    if (motifFilter) {
      const motif = detectComplaintMotif(complaint);
      if (motif !== motifFilter) return false;
    }

    return true;
  });

  const columns = [
    {
      title: 'Référence',
      dataIndex: 'reference',
      key: 'reference',
      render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>
    },
    {
      title: 'Titre',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true
    },
    {
      title: 'Motif',
      key: 'motif',
      render: (_: any, record: Complaint) => {
        const motif = detectComplaintMotif(record);
        return <Tag color="geekblue">{motif}</Tag>;
      }
    },
    {
      title: 'Client',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer: any) => 
        customer ? `${customer.first_name} ${customer.last_name}` : '-'
    },
    {
      title: 'Priorité',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: keyof typeof priorityMap) => {
        const config = priorityMap[priority];
        return config ? (
          <Tag color={config.color}>{config.label}</Tag>
        ) : priority;
      }
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: keyof typeof statusMap) => {
        const config = statusMap[status];
        return config ? (
          <Tag color={config.color}>{config.label}</Tag>
        ) : status;
      }
    },
    {
      title: 'Date de création',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string, record: any) => {
        // Essayer plusieurs formats de champs
        const dateValue = date || record.createdAt || record.created_at;
        if (!dateValue) return '-';
        try {
          const parsedDate = dayjs(dateValue);
          if (!parsedDate.isValid()) return '-';
          return parsedDate.format('DD/MM/YYYY HH:mm');
        } catch (error) {
          return '-';
        }
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (record: Complaint) => (
        <Space size="small">
          <Tooltip title="Voir les détails">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => navigate(`/reclamations/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => navigate(`/reclamations/${record.id}/modifier`)}
            />
          </Tooltip>
          {isAdmin && (
            <Popconfirm
              title="Supprimer la réclamation"
              description={`Êtes-vous sûr de vouloir supprimer "${record.reference}" ?`}
              onConfirm={() => record.id && handleDelete(record.id)}
              okText="Oui"
              cancelText="Non"
            >
              <Tooltip title="Supprimer">
                <Button type="text" icon={<DeleteOutlined />} danger />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const handleExport = () => {
    try {
      setExporting(true);
      const rows = filteredComplaints.map(c => ({
        reference: c.reference || `#${c.id}`,
        titre: c.subject || '',
        motif: detectComplaintMotif(c),
        client: c.customer ? `${c.customer.first_name || ''} ${c.customer.last_name || ''}`.trim() : 'N/A',
        email_client: c.customer?.email || '',
        priorite: priorityMap[c.priority]?.label || c.priority,
        statut: statusMap[c.status]?.label || c.status,
        date_creation: c.created_at ? dayjs(c.created_at).format('DD/MM/YYYY HH:mm') : '',
        description: c.description || '',
        resolution: c.resolution || '',
      }));

      const headers = [
        { header: 'Référence', key: 'reference' },
        { header: 'Titre / Objet', key: 'titre' },
        { header: 'Motif (Problème Récurrent)', key: 'motif' },
        { header: 'Client', key: 'client' },
        { header: 'Email Client', key: 'email_client' },
        { header: 'Priorité', key: 'priorite' },
        { header: 'Statut', key: 'statut' },
        { header: 'Date de Création', key: 'date_creation' },
        { header: 'Description', key: 'description' },
        { header: 'Résolution', key: 'resolution' },
      ];

      exportToCSV(rows, headers, `reclamations_${new Date().toISOString().slice(0, 10)}.csv`);
      message.success('Export des réclamations généré avec succès');
    } catch (err) {
      console.error('Erreur export réclamations:', err);
      message.error("Erreur lors de l'export des réclamations");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600 }}>
            Gestion des Réclamations
          </h1>
        </Col>
        <Col>
          <Space>
            <Button 
              icon={<DownloadOutlined />} 
              onClick={handleExport}
              loading={exporting}
            >
              Exporter
            </Button>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchComplaints}
              loading={loading}
            >
              Actualiser
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => navigate('/reclamations/nouvelle')}
            >
              Nouvelle Réclamation
            </Button>
          </Space>
        </Col>
      </Row>

      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input.Search
              placeholder="Rechercher des réclamations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filtrer par motif (problème récurrent)"
              value={motifFilter || undefined}
              onChange={(val) => setMotifFilter(val || '')}
              allowClear
              style={{ width: '100%' }}
            >
              <Select.Option value="">Tous les motifs</Select.Option>
              {RECURRENT_MOTIFS.map((m) => (
                <Select.Option key={m.value} value={m.label}>{m.label}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filtrer par statut"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Select.Option value="open">Ouvert</Select.Option>
              <Select.Option value="in_progress">En cours</Select.Option>
              <Select.Option value="resolved">Résolu</Select.Option>
              <Select.Option value="rejected">Rejeté</Select.Option>
              <Select.Option value="on_hold">En attente</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filtrer par priorité"
              value={priorityFilter}
              onChange={setPriorityFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Select.Option value="low">Basse</Select.Option>
              <Select.Option value="medium">Moyenne</Select.Option>
              <Select.Option value="high">Haute</Select.Option>
              <Select.Option value="critical">Critique</Select.Option>
            </Select>
          </Col>
        </Row>
      </Card>

      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredComplaints}
            rowKey="id"
            pagination={{
              total: filteredComplaints.length,
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} réclamations`,
            }}
            locale={{
              emptyText: 'Aucune réclamation trouvée'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default ComplaintsPage;
